// UploadPopup.js
import React, { useState, useEffect } from 'react';
import { CATEGORIES } from '../../Models/datamodel.js';
import AIUploadSection from './AIUploadSection.js';

const UploadPopup = ({ onClose, onUpload, editItem }) => {
  // Toggle state
  const [uploadMode, setUploadMode] = useState('manual'); // 'manual' or 'ai'
  
  // Manual upload states
  const [glbFile, setGlbFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  
  // AI upload states
  const [images, setImages] = useState([]);
  const [dimensions, setDimensions] = useState({ height: '', unit: 'meters' });
  
  // Common states
  const [itemName, setItemName] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);

  useEffect(() => {
    if (editItem) {
      setItemName(editItem.itemName);
      setGlbFile(editItem.glbUrl);
      setPreviewImage(editItem.imageUrl);
      setCategory(editItem.category && CATEGORIES.includes(editItem.category) ? editItem.category : CATEGORIES[0]);
    }
  }, [editItem]);

  const handleGlbChange = (event) => {
    setGlbFile(event.target.files[0]);
  };

  const handleImageChange = (event) => {
    setPreviewImage(event.target.files[0]);
  };

  const handleImagesChange = (event) => {
    const files = Array.from(event.target.files);
    setImages(files);
  };

  const handleAIImagesChange = (files) => {
    setImages(files);
  };

  const handleDimensionsChange = (newDimensions) => {
    setDimensions(newDimensions);
  };

  const handleGlbDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    if (file && file.name.endsWith('.glb')) {
      setGlbFile(file);
    }
  };

  const handleImageDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setPreviewImage(file);
    }
  };

  const handleImagesDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const files = Array.from(event.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    setImages(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleSubmit = () => {
    if (!itemName.trim()) {
      alert('Please enter an item name.');
      return;
    }

    if (uploadMode === 'manual') {
      // Manual upload validation
      if (!glbFile) {
        alert('Please select a GLB file.');
        return;
      }
      
      onUpload({ glbFile, previewImage, itemName, category });
    } else {
      // AI upload validation
      if (images.length === 0) {
        alert('Please select at least one image.');
        return;
      }
      
      onUpload({ mode: 'ai', images, itemName, category, dimensions });
    }
    
    onClose();
  };

  const isSubmitDisabled = uploadMode === 'manual' 
    ? !itemName.trim() || !glbFile 
    : !itemName.trim() || images.length === 0;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          width: '460px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          minHeight: '550px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
              {editItem ? 'Edit Item' : 'Upload Item'}
            </h3>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#666',
              }}
            >
              ✕
            </button>
          </div>

          {/* Toggle Buttons */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', backgroundColor: '#f5f5f5', borderRadius: '8px', padding: '4px' }}>
              <button
                onClick={() => setUploadMode('manual')}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: uploadMode === 'manual' ? '#8A63FF' : 'transparent',
                  color: uploadMode === 'manual' ? 'white' : '#666',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: uploadMode === 'manual' ? 'bold' : 'normal',
                  transition: 'all 0.2s ease',
                }}
              >
                Manual Upload
              </button>
              <button
                onClick={() => setUploadMode('ai')}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: uploadMode === 'ai' ? '#8A63FF' : 'transparent',
                  color: uploadMode === 'ai' ? 'white' : '#666',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: uploadMode === 'ai' ? 'bold' : 'normal',
                  transition: 'all 0.2s ease',
                }}
              >
                AI Image to 3D
              </button>
            </div>
          </div>

          {/* Item Name - Always visible */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
              Item Name (Required)
            </label>
            <input
              type="text"
              placeholder="Enter item name"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Category - Always visible */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
              Category (Required)
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                boxSizing: 'border-box',
              }}
            >
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Conditional Content Based on Mode */}
          {uploadMode === 'manual' ? (
            <>
              {/* GLB File Upload */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                  GLB Model (Required)
                </label>
                <div
                  onDrop={handleGlbDrop}
                  onDragOver={handleDragOver}
                  style={{
                    border: '2px dashed #ccc',
                    padding: '25px',
                    textAlign: 'center',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    minHeight: '120px',
                  }}
                >
                  <input
                    type="file"
                    accept=".glb"
                    onChange={handleGlbChange}
                    style={{ display: 'none' }}
                    id="glb-upload"
                  />
                  <label htmlFor="glb-upload" style={{ cursor: 'pointer', display: 'block' }}>
                    <span className="material-icons" style={{ fontSize: '24px', color: '#666' }}>cloud_upload</span>
                    <span style={{ display: 'block', marginBottom: '5px', color: '#000' }}>Upload GLB File</span>
                    <span style={{ color: '#666' }}>
                      {glbFile ? (typeof glbFile === 'string' ? 'Current GLB file' : glbFile.name) : 'Drag & drop or click to browse'}
                    </span>
                  </label>
                </div>
              </div>

              {/* Preview Image Upload */}
              <div style={{ marginBottom: '40px' }}>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px', color: '#333' }}>
                  Preview Image (Optional)
                </label>
                <div
                  onDrop={handleImageDrop}
                  onDragOver={handleDragOver}
                  style={{
                    border: '2px dashed #ccc',
                    padding: '25px',
                    textAlign: 'center',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    minHeight: '120px',
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                    id="image-upload"
                  />
                  <label htmlFor="image-upload" style={{ cursor: 'pointer', display: 'block' }}>
                    <span className="material-icons" style={{ fontSize: '24px', color: '#666' }}>image</span>
                    <span style={{ display: 'block', marginBottom: '5px', color: '#000' }}>Upload Image</span>
                    <span style={{ color: '#666' }}>
                      {previewImage ? (typeof previewImage === 'string' ? 'Current image' : previewImage.name) : 'Drag & drop or click to browse'}
                    </span>
                  </label>
                </div>
              </div>
            </>
          ) : (
            <AIUploadSection 
              images={images} 
              onImagesChange={handleAIImagesChange}
              dimensions={dimensions}
              onDimensionsChange={handleDimensionsChange}
            />
          )}
        </div>

        {/* Submit Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              background: '#f0f0f0',
              color: '#333',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: isSubmitDisabled ? '#A78BFF' : '#8A63FF',
              color: 'white',
              cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: 1,
            }}
          >
            {editItem ? 'Update Item' : uploadMode === 'manual' ? 'Add to Store' : 'Generate 3D Model'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadPopup;